import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import Chip from "@material-ui/core/Chip";
import Container from "@material-ui/core/Container";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import InputAdornment from "@material-ui/core/InputAdornment";
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import AttachMoney from "@material-ui/icons/AttachMoney";
import LocalMall from "@material-ui/icons/LocalMall";
import DirectionsWalk from "@material-ui/icons/DirectionsWalk";
import Event from "@material-ui/icons/Event";
import EditIcon from "@material-ui/icons/Edit";
import People from "@material-ui/icons/People";
import Snackbar from "components/Snackbar/Snackbar.js";
import LoadingAnimation from "components/Animations/loading";
import { useAsyncEffect } from "use-async-effect";
import { redirectToLogout } from "common/SharedFn";
import { getMonthlyPackage, updateMonthlyPackage } from "../../network/ApiAxios";

// The tenant's monthly prepaid package (LNDRY SOW R9, item 8): what customers see and pay,
// whether it is offered in the app, and an editor for it. Customer purchases are on the
// separate Package Subscribers page.

const formatBd = (value) => `${(Number(value) || 0).toFixed(3)} BD`;

const DEFAULT_PACKAGE = {
  nameEn: "Monthly Package",
  nameAr: "الباقة الشهرية",
  descriptionEn: "",
  descriptionAr: "",
  price: 35.5,
  bagsIncluded: 4,
  shoesIncluded: 3,
  validityDays: 30,
  isEnabled: 0,
};

// The same rules the backend applies (MonthlyPackagePortalService.validate), checked
// before saving so each problem shows under its own field.
const validate = (form) => {
  const errors = {};
  const number = (value) => (value === "" || value === null || value === undefined ? NaN : Number(value));
  if (!String(form.nameEn || "").trim()) errors.nameEn = "English name is required.";
  if (!(number(form.price) > 0)) errors.price = "Price must be above zero.";
  const bags = number(form.bagsIncluded);
  const shoes = number(form.shoesIncluded);
  if (!(bags >= 0) || !Number.isInteger(bags)) errors.bagsIncluded = "A whole number, 0 or more.";
  if (!(shoes >= 0) || !Number.isInteger(shoes)) errors.shoesIncluded = "A whole number, 0 or more.";
  if (!errors.bagsIncluded && !errors.shoesIncluded && bags + shoes === 0) {
    errors.bagsIncluded = "Include at least one bag or shoe cleaning.";
  }
  const days = number(form.validityDays);
  if (!(days >= 1 && days <= 365) || !Number.isInteger(days)) errors.validityDays = "Between 1 and 365 days.";
  return errors;
};

const toRequest = (form, isEnabled) => ({
  nameEn: String(form.nameEn || "").trim(),
  nameAr: form.nameAr,
  descriptionEn: form.descriptionEn,
  descriptionAr: form.descriptionAr,
  price: Number(form.price),
  bagsIncluded: Number(form.bagsIncluded),
  shoesIncluded: Number(form.shoesIncluded),
  validityDays: Number(form.validityDays),
  isEnabled: isEnabled ? 1 : 0,
});

const MonthlyPackage = () => {
  const classes = useStyles();
  const history = useHistory();
  const [pkg, setPkg] = useState(null);
  const [vatPercentage, setVatPercentage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [errors, setErrors] = useState({});
  const [confirmAvailability, setConfirmAvailability] = useState(null);
  const [notifMessage, setNotifMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [success, setSuccess] = useState(false);

  const notify = (message, isError) => {
    setNotifMessage(message);
    if (isError) {
      setShowError(true);
      setTimeout(() => setShowError(false), 4000);
    } else {
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    }
  };

  // A failed call shows its reason; an expired login goes back to the login page.
  const handleFailure = (result, fallback) => {
    notify(result && result.description ? result.description : fallback, true);
    if (result && result.code === -2) redirectToLogout();
  };

  const load = async () => {
    setIsLoading(true);
    try {
      const response = await getMonthlyPackage();
      if (response.data.result.isSuccess) {
        const data = response.data.data || {};
        setPkg(data.package || null);
        setVatPercentage(data.vatPercentage === undefined ? null : data.vatPercentage);
      } else {
        handleFailure(response.data.result, "Failed to load the monthly package");
      }
    } catch (ex) {
      notify("Failed to load the monthly package", true);
    }
    setIsLoading(false);
  };

  useAsyncEffect(load, []);

  const save = async (form, isEnabled, message) => {
    setIsSaving(true);
    try {
      const response = await updateMonthlyPackage(toRequest(form, isEnabled));
      if (response.data.result.isSuccess) {
        setPkg(response.data.data);
        notify(message);
        setIsSaving(false);
        return true;
      }
      handleFailure(response.data.result, "Failed to save the monthly package");
    } catch (ex) {
      notify("Failed to save the monthly package", true);
    }
    setIsSaving(false);
    return false;
  };

  const openEditor = () => {
    const source = pkg || DEFAULT_PACKAGE;
    setErrors({});
    setEditing({
      nameEn: source.nameEn || "",
      nameAr: source.nameAr || "",
      descriptionEn: source.descriptionEn || "",
      descriptionAr: source.descriptionAr || "",
      price: source.price,
      bagsIncluded: source.bagsIncluded,
      shoesIncluded: source.shoesIncluded,
      validityDays: source.validityDays,
    });
  };

  const saveEdit = async () => {
    const found = validate(editing);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    const saved = await save(editing, pkg ? pkg.isEnabled === 1 : false,
      "Package saved. Changes apply to new purchases only.");
    if (saved) setEditing(null);
  };

  const setAvailability = async () => {
    const enable = confirmAvailability;
    const saved = await save(pkg, enable, enable ? "The package is now offered in the app." : "The package is now hidden from the app.");
    if (saved) setConfirmAvailability(null);
  };

  const withVat = (price) => (vatPercentage === null ? null : Math.round(price * (100 + vatPercentage) * 10) / 1000);
  const set = (field) => (e) => setEditing({ ...editing, [field]: e.target.value });

  const textField = (label, field, extra = {}) => (
    <TextField
      fullWidth
      variant="outlined"
      size="small"
      margin="dense"
      label={label}
      value={editing[field]}
      onChange={set(field)}
      error={!!errors[field]}
      helperText={errors[field] || extra.helperText}
      multiline={!!extra.multiline}
      rows={extra.multiline ? 3 : undefined}
      type={extra.type || "text"}
      InputLabelProps={{ shrink: true }}
      InputProps={extra.InputProps}
      inputProps={{ dir: extra.rtl ? "rtl" : "ltr", step: extra.step, min: extra.min }}
    />
  );

  if (isLoading) {
    return (
      <Container maxWidth={false} component={Box} marginTop="-4.5rem" classes={{ root: classes.containerRoot }}>
        <Card className={classes.cardRoot}>
          <Box display="flex" justifyContent="center" padding="2rem">
            <div style={{ width: "20%" }}><LoadingAnimation /></div>
          </Box>
        </Card>
      </Container>
    );
  }

  const live = pkg && pkg.isEnabled === 1;
  const customerTotal = pkg ? withVat(pkg.price) : null;

  return (
    <Container maxWidth={false} component={Box} marginTop="-4.5rem" classes={{ root: classes.containerRoot }}>
      {!pkg ? (
        <Card className={classes.cardRoot}>
          <Box padding="2rem" textAlign="center">
            <Typography variant="h3" style={{ fontWeight: 700 }}>No monthly package yet</Typography>
            <Typography variant="body2" color="textSecondary" style={{ margin: "0.5rem 0 1.25rem" }}>
              Set it up here. It stays hidden from customers until you switch it on.
            </Typography>
            <Button variant="contained" color="primary" onClick={openEditor} className={classes.actionButton}>
              Set up the package
            </Button>
          </Box>
        </Card>
      ) : (
        <>
          {/* Status and actions */}
          <Card className={classes.headerCard}>
            <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" style={{ gap: 12 }}>
              <Box minWidth={0}>
                <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 10 }}>
                  <Typography variant="h3" style={{ fontWeight: 700, margin: 0 }}>{pkg.nameEn}</Typography>
                  <Chip
                    size="small"
                    label={live ? "Offered in the app" : "Hidden from customers"}
                    className={classes.statusChip}
                    style={{ backgroundColor: live ? "#e8f5e9" : "#f1f3f5", color: live ? "#2e7d32" : "#6b7280" }}
                  />
                </Box>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                  Changes apply to new purchases only. Customers who already bought keep what they paid for.
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
                <Box display="flex" alignItems="center">
                  <Switch
                    color="primary"
                    checked={live}
                    disabled={isSaving}
                    onChange={() => setConfirmAvailability(!live)}
                    inputProps={{ "aria-label": "Offered in the app" }}
                  />
                  <Typography variant="body2" style={{ fontWeight: 600 }}>Offered in the app</Typography>
                </Box>
                <Button variant="outlined" color="primary" startIcon={<People />} className={classes.actionButton}
                  onClick={() => history.push("/admin/monthly-package-subscribers")}>
                  Subscribers
                </Button>
                <Button variant="contained" color="primary" startIcon={<EditIcon />} className={classes.actionButton}
                  onClick={openEditor}>
                  Edit package
                </Button>
              </Box>
            </Box>
          </Card>

          {/* Key figures */}
          <Grid container spacing={2} style={{ marginBottom: "0.5rem" }}>
            {[
              {
                label: "Customers pay",
                value: customerTotal === null ? formatBd(pkg.price) : formatBd(customerTotal),
                note: customerTotal === null ? "excl. VAT" : `${formatBd(pkg.price)} + ${vatPercentage}% VAT`,
                icon: <AttachMoney />,
                color: "#5e72e4",
              },
              { label: "Bags", value: pkg.bagsIncluded, note: "any bag service", icon: <LocalMall />, color: "#11cdef" },
              { label: "Shoe cleanings", value: pkg.shoesIncluded, note: "1 per pair", icon: <DirectionsWalk />, color: "#fb6340" },
              { label: "Valid for", value: `${pkg.validityDays} days`, note: "from purchase, no carry-over", icon: <Event />, color: "#2dce89" },
            ].map((stat) => (
              <Grid item xs={12} sm={6} md={3} key={stat.label}>
                <Card className={classes.statCard}>
                  <Box className={classes.iconShape} style={{ backgroundColor: stat.color }}>{stat.icon}</Box>
                  <Box minWidth={0}>
                    <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>{stat.label}</Typography>
                    <Typography variant="h4" style={{ fontWeight: 700, lineHeight: 1.2 }}>{stat.value}</Typography>
                    <Typography variant="caption" color="textSecondary">{stat.note}</Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* What customers read in the app */}
          <Card className={classes.cardRoot}>
            <Box padding="1.25rem 1.5rem 0.5rem">
              <Typography variant="h4" style={{ fontWeight: 700 }}>What customers see</Typography>
            </Box>
            <Grid container>
              <Grid item xs={12} md={6}>
                <Box padding="0.75rem 1.5rem 1.5rem">
                  <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>ENGLISH</Typography>
                  <Typography variant="h5" style={{ fontWeight: 700, marginTop: 4 }}>{pkg.nameEn}</Typography>
                  <Typography variant="body2" color={pkg.descriptionEn ? "textPrimary" : "textSecondary"} style={{ whiteSpace: "pre-line", marginTop: 4 }}>
                    {pkg.descriptionEn || "No description"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6} className={classes.arabicColumn}>
                <Box padding="0.75rem 1.5rem 1.5rem" dir="rtl" textAlign="right">
                  <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>العربية</Typography>
                  <Typography variant="h5" style={{ fontWeight: 700, marginTop: 4 }}>{pkg.nameAr || "—"}</Typography>
                  <Typography variant="body2" color={pkg.descriptionAr ? "textPrimary" : "textSecondary"} style={{ whiteSpace: "pre-line", marginTop: 4 }}>
                    {pkg.descriptionAr || "لا يوجد وصف"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </>
      )}

      {/* Editor */}
      <Dialog open={!!editing} onClose={() => !isSaving && setEditing(null)} maxWidth="md" fullWidth>
        <DialogTitle disableTypography>
          <Typography variant="h3" style={{ fontWeight: 700, margin: 0 }}>{pkg ? "Edit package" : "Set up the package"}</Typography>
          <Typography variant="body2" color="textSecondary">Changes apply to new purchases only.</Typography>
        </DialogTitle>
        {editing && (
          <DialogContent dividers>
            <Typography variant="h5" className={classes.sectionTitle}>Text in the app</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {textField("Name (English)", "nameEn")}
                {textField("Description (English)", "descriptionEn", { multiline: true })}
              </Grid>
              <Grid item xs={12} md={6}>
                {textField("Name (Arabic)", "nameAr", { rtl: true })}
                {textField("Description (Arabic)", "descriptionAr", { multiline: true, rtl: true })}
              </Grid>
            </Grid>
            <Divider style={{ margin: "1rem 0" }} />
            <Typography variant="h5" className={classes.sectionTitle}>Price and allowance</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                {textField("Price (excl. VAT)", "price", {
                  type: "number",
                  step: "0.001",
                  min: 0,
                  InputProps: { endAdornment: <InputAdornment position="end">BD</InputAdornment> },
                  helperText: withVat(Number(editing.price) || 0) === null ? " "
                    : `Customers pay ${formatBd(withVat(Number(editing.price) || 0))}`,
                })}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {textField("Bags", "bagsIncluded", { type: "number", step: "1", min: 0, helperText: " " })}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {textField("Shoe cleanings", "shoesIncluded", { type: "number", step: "1", min: 0, helperText: " " })}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {textField("Valid for", "validityDays", {
                  type: "number",
                  step: "1",
                  min: 1,
                  InputProps: { endAdornment: <InputAdornment position="end">days</InputAdornment> },
                  helperText: " ",
                })}
              </Grid>
            </Grid>
          </DialogContent>
        )}
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setEditing(null)} disabled={isSaving} className={classes.actionButton}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={saveEdit} disabled={isSaving} className={classes.actionButton}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Offer / hide confirmation */}
      <Dialog open={confirmAvailability !== null} onClose={() => !isSaving && setConfirmAvailability(null)} maxWidth="xs" fullWidth>
        <DialogTitle disableTypography>
          <Typography variant="h4" style={{ fontWeight: 700, margin: 0 }}>
            {confirmAvailability ? "Offer the package in the app?" : "Hide the package from the app?"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmAvailability
              ? "Customers will see it on the home screen and can buy it right away."
              : "Customers can no longer buy it. Anyone who already bought one keeps using it until it ends."}
          </Typography>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setConfirmAvailability(null)} disabled={isSaving} className={classes.actionButton}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={setAvailability} disabled={isSaving} className={classes.actionButton}>
            {isSaving ? "Saving..." : confirmAvailability ? "Offer it" : "Hide it"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar place="tr" color="success" message={notifMessage} open={success} closeNotification={() => setSuccess(false)} close />
      <Snackbar place="tr" color="rose" message={notifMessage} open={showError} closeNotification={() => setShowError(false)} close />
    </Container>
  );
};

export default MonthlyPackage;

// Same card language as the POS sales screen.
const useStyles = makeStyles((theme) => ({
  containerRoot: {
    [theme.breakpoints.up("md")]: {
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  cardRoot: {
    marginBottom: "1.25rem",
    borderRadius: "15px",
    boxShadow: "0 0 2rem 0 rgba(136, 152, 170, .15)",
  },
  // First thing on the page, so it straddles the header band like the POS filter row.
  headerCard: {
    padding: "1.25rem 1.5rem",
    marginBottom: "1.25rem",
    borderRadius: "15px",
    boxShadow: "0 0 2rem 0 rgba(136, 152, 170, .15)",
    position: "relative",
    zIndex: 1,
  },
  statCard: {
    borderRadius: "15px",
    height: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: "1rem",
    "&&": {
      marginBottom: 0,
    },
  },
  iconShape: {
    width: "36px",
    height: "36px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    marginRight: "0.875rem",
    color: "#fff",
  },
  statusChip: {
    fontWeight: 700,
    borderRadius: "6px",
  },
  arabicColumn: {
    [theme.breakpoints.up("md")]: {
      borderLeft: "1px solid #edf0f7",
    },
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: "0.25rem",
  },
  actionButton: {
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "none",
    whiteSpace: "nowrap",
  },
  dialogActions: {
    padding: "0.75rem 1.5rem",
  },
}));
